import { FormApiService, QueryApiService, QueryResult } from 'src/common/api-service';
import { AuditableEntity } from '../common';
import { JobDomain } from './job-domains-api';
import { JobRoleJobSkill } from './job-roles-job-skills-api';
import { AxiosError } from 'axios';

export interface JobRole extends AuditableEntity<string> {
  name: string;
  domainId: string;
  domain: JobDomain;
  skills: JobRoleJobSkill[];
  order?: number;
  isActive: boolean;
  linkedInRoles: LinkedInJobRoleJobs2LiveJobRole[];
}

export interface LinkedInJobRole extends AuditableEntity<string> {
  name: string;
  domainId: string;
  domain: JobDomain;
  jobs2LiveRoles: LinkedInJobRoleJobs2LiveJobRole[];
}

export interface LinkedInJobRoleJobs2LiveJobRole extends AuditableEntity<string> {
  jobs2LiveJobRoleId: string;
  jobs2LiveJobRole: JobRole;

  linkedInJobRoleId: string;
  linkedInJobRole: LinkedInJobRole;
}

export interface RoleSearchResultItemDto {
  id: string;
  label: string;
  score: number;
}

export interface RoleSkillSearchResultItemDto {
  roleId: string;
  skillId: string;
  skillLabel: string;
  score: number;
}

export interface RoleSkillsResultItemDto {
  roleId: string;
  skillId: string;
  skillLabel: string;
  order: number;
}

type GetRoleSkillsParams = {
  roleId: string;
  size?: number;
  description?: string;
  selectedSkills?: string[];
};

export class JobRoleApiService extends QueryApiService<JobRole> {
  constructor() {
    super('jobRoles');
  }

  public async search(input: string, size: number = 10): Promise<QueryResult<RoleSearchResultItemDto>> {
    try {
      return await this.get<QueryResult<RoleSearchResultItemDto>>(`search`, { input, size });
    } catch (e) {
      const axiosError = e as AxiosError<QueryResult<RoleSearchResultItemDto>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async getSkills(params: GetRoleSkillsParams): Promise<QueryResult<RoleSkillsResultItemDto>> {
    const { roleId, size = 25, description, selectedSkills } = params;

    try {
      return await this.get<QueryResult<RoleSkillsResultItemDto>>(`${encodeURIComponent(roleId)}/skills`, { size, description, selectedSkills });
    } catch (e) {
      const axiosError = e as AxiosError<QueryResult<RoleSkillsResultItemDto>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async searchSkills(roleId: string, input: string, size: number = 10): Promise<QueryResult<RoleSkillSearchResultItemDto>> {
    try {
      return await this.get<QueryResult<RoleSkillSearchResultItemDto>>(`${encodeURIComponent(roleId)}/skills/search`, { input, size });
    } catch (e) {
      const axiosError = e as AxiosError<QueryResult<RoleSkillSearchResultItemDto>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }
}

export class JobRoleFormApiService extends FormApiService {
  constructor() {
    super('jobRoles');
  }
}
